import { RefObject, useEffect, useLayoutEffect, useRef } from 'react'

type UseClickOutsideOptions = {
  /** Run the callback even when the click target has no layout box */
  skipVisibilityCheck?: boolean
}

/**
 * Calls `callback` for document clicks that land outside `ref.current`.
 * Clicks that predate the listener registration are ignored, so the click that
 * opens a component cannot immediately close it. Pass `null` as the ref while
 * the component is closed so the listener registers on open.
 */
export const useClickOutside = (
  ref: RefObject<HTMLElement> | null,
  callback: (event: MouseEvent) => void,
  { skipVisibilityCheck = false }: UseClickOutsideOptions = {}
) => {
  const callbackRef = useRef(callback)

  useLayoutEffect(() => {
    callbackRef.current = callback
  })

  useEffect(() => {
    if (!ref) return

    // Same clock as Event.timeStamp in browsers and jsdom
    const registeredAt = new Event('hsds-click-outside-registered').timeStamp

    const handleClick = (e: MouseEvent) => {
      // React 17 can flush the render that mounts `ref` before the opening
      // click reaches document. `<=` because Safari and Firefox clamp
      // timeStamp to 1ms.
      if (e.timeStamp <= registeredAt) return

      const target = e.target as HTMLElement

      if (
        ref.current &&
        !ref.current.contains(target) &&
        (skipVisibilityCheck || isVisible(target))
      ) {
        callbackRef.current(e)
      }
    }

    document.addEventListener('click', handleClick)

    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [ref, skipVisibilityCheck])
}

function isVisible(elem: HTMLElement) {
  if (process.env.NODE_ENV === 'test') return true

  return (
    !!elem &&
    !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length)
  )
}
